import { NextFunction, Request, Response } from 'express';
import Container, { Service } from 'typedi';
import { AttachmentService } from '../services/attachment.service';
import { jsonStatus, status } from '../../../utils/helpers/api.responses';
import { HttpException } from '../../../utils/exceptions/httpException';
import { AttachmentType } from '../interfaces/attcahment.interface';

@Service()
export class UploadFileController {
    public uploadService = Container.get(AttachmentService);

    public uploadData = async (req: Request, res: Response, next: NextFunction) => {
        const t = req.t || ((key: string, options?: any) => key);

        try {
            const files = req.files as { [fieldname: string]: Express.Multer.File[] } | undefined;

            if (!files || Object.keys(files).length === 0) {
                throw new HttpException(
                    status.BadRequest,
                    t('Upload.no_file_uploaded')
                );
            }

            const profileImages = files['profileImage'] || [];
            const certificates = files['certificate'] || [];
            const documents = files['document'] || [];
            const identifications = files['identification'] || [];
            const chatMedias = files['chatMedia'] || []

            if (!profileImages.length && !certificates.length && !documents.length && !identifications.length && !chatMedias.length) {
                throw new HttpException(
                    status.BadRequest,
                    t('Upload.no_file_uploaded')
                );
            }

            const { moduleName } = req.query;
            const allowedModules = [
                AttachmentType.PROFILE_IMAGE,
                AttachmentType.CERTIFICATE,
                AttachmentType.DOCUMENT,
                AttachmentType.IDENTIFICATION,
                AttachmentType.CHATMEDIA

            ];
            if (!moduleName || typeof moduleName !== 'string' || !allowedModules.includes(moduleName as AttachmentType)) {
                throw new HttpException(
                    status.BadRequest,
                    t('General.invalid', { field: t('Upload.moduleName') })
                );
            }

            const uploadedBy = req.user._id;
            const entityId = uploadedBy;
            const entityModel = 'User';

            const enrichedFiles = [
                ...profileImages.map(file => ({
                    ...file,
                    moduleType: AttachmentType.PROFILE_IMAGE,
                })),
                ...certificates.map(file => ({
                    ...file,
                    moduleType: AttachmentType.CERTIFICATE,
                })),
                ...documents.map(file => ({
                    ...file,
                    moduleType: AttachmentType.DOCUMENT,
                })),
                ...identifications.map(file => ({
                    ...file,
                    moduleType: AttachmentType.IDENTIFICATION
                })),
                ...chatMedias.map(file => ({
                    ...file,
                    moduleType: AttachmentType.CHATMEDIA
                }))
            ];

            const result = await this.uploadService.handleUpload(
                enrichedFiles,
                {
                    moduleName: moduleName as AttachmentType,
                    moduleType: enrichedFiles[0]?.moduleType,
                    uploadedBy,
                    entityId,
                    entityModel,
                },
                req,
                t
            );

            const hasFailures = result.failedFiles.length > 0;

            return res.status(hasFailures ? status.BadRequest : status.OK).json({
                status: hasFailures ? jsonStatus.BadRequest : jsonStatus.OK,
                message: hasFailures
                    ? t('Upload.partial_success')
                    : t('Upload.imagesAdded'),
                ...result,
            });
        } catch (error) {
            next(error);
        }
    };

    public getImages = async (req: Request, res: Response, next: NextFunction) => {
        const t = req.t || ((key: string, options?: any) => key);

        try {
            const moduleName = req.query.moduleName as string | undefined;
            const data = await this.uploadService.getImages(t, moduleName);

            return res.status(status.OK).json({
                status: jsonStatus.OK,
                data,
                message: t('General.images_fetched'),
            });
        } catch (error) {
            next(error);
        }
    };

    public deleteImages = async (req: Request, res: Response, next: NextFunction) => {
        const t = req.t || ((key: string, options?: any) => key);

        try {
            const { ids } = req.body;

            if (!Array.isArray(ids) || ids.length === 0) {
                throw new HttpException(
                    status.BadRequest,
                    t('General.error')
                );
            }

            const deletedIds = await this.uploadService.deleteDocuments(ids, t);

            return res.status(status.OK).json({
                status: jsonStatus.OK,
                message: t('Upload.images_deleted'),
                deletedIds,
            });
        } catch (error) {
            next(error);
        }
    };

    public deleteTemporary = async (req: Request, res: Response, next: NextFunction) => {
        const t = req.t || ((key: string, options?: any) => key);

        try {
            await this.uploadService.deleteTemporaryFiles(t);
            return res.status(status.OK).json({
                status: jsonStatus.OK,
                message: t('General.delete_success', { field: t('General.image') }),
            });
        } catch (error) {
            next(error);
        }
    };

    public getById = async (req: Request, res: Response, next: NextFunction) => {
        const t = req.t || ((key: string, options?: any) => key);

        try {
            const { id } = req.params;

            if (!id) {
                throw new HttpException(
                    status.BadRequest,
                    t('General.error')
                );
            }

            const data = await this.uploadService.getById(id, t);

            return res.status(status.OK).json({
                status: jsonStatus.OK,
                data,
                message: t('General.image_fetched'),
            });
        } catch (error) {
            next(error);
        }
    };
}